import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../services/api';

// Função para obter nome de exibição com fallback para número formatado
const getDisplayName = (contact) => {
  const name = contact?.name || contact?.push_name;

  if (name && name.length > 1 && !/^[.\-_\s!@#$%^&*()]+$/.test(name)) {
    return name;
  }

  const phone = contact?.phone || '';
  if (phone.length >= 10) {
    if (phone.startsWith('55') && phone.length >= 12) {
      const ddd = phone.slice(2, 4);
      const part1 = phone.slice(4, phone.length - 4);
      const part2 = phone.slice(-4);
      return `+55 (${ddd}) ${part1}-${part2}`;
    }
    return phone;
  }

  return phone || 'Contato';
};

// Paleta de cores para avatares
const AVATAR_COLORS = [
  { bg: 'bg-primary/20',   text: 'text-primary'       },
  { bg: 'bg-blue-500/20',  text: 'text-blue-400'       },
  { bg: 'bg-orange-500/20',text: 'text-orange-400'     },
  { bg: 'bg-teal-500/20',  text: 'text-teal-400'       },
  { bg: 'bg-indigo-500/20',text: 'text-indigo-400'     },
  { bg: 'bg-cyan-500/20',  text: 'text-cyan-400'       },
  { bg: 'bg-pink-500/20',  text: 'text-pink-400'       },
  { bg: 'bg-yellow-500/20',text: 'text-yellow-400'     },
  { bg: 'bg-purple-500/20',text: 'text-purple-400'     },
];

const getAvatarColor = (str) => {
  let hash = 0;
  for (let i = 0; i < (str || '').length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) & 0xffff;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// Componente de Avatar do Contato
const ContactAvatar = ({ name, phone, imageUrl, size = 'md' }) => {
  const displayName = name || phone || '?';
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const color = getAvatarColor(displayName);
  const sizeClass = size === 'lg' ? 'w-16 h-16 text-xl' : 'w-10 h-10 text-sm';

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={displayName}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  }

  return (
    <div className={`${sizeClass} rounded-full ${color.bg} ${color.text} flex items-center justify-center font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
};

// Modal de Informações do Contato
const ContactInfoModal = ({ contact, isOpen, onClose, targetDbName }) => {
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);
  const [stoppingAgent, setStoppingAgent] = React.useState(false);
  const [stopAgentResult, setStopAgentResult] = React.useState(null);
  const [checkingStatus, setCheckingStatus] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setStopAgentResult(null);
      const cleanPhone = contact?.phone?.replace(/\D/g, '');
      if (cleanPhone) {
        setCheckingStatus(true);
        api.get(`/blocking/ai-status/${cleanPhone}`)
          .then(res => {
            if (res.data?.is_ai_blocked) setStopAgentResult('success');
          })
          .catch(() => {})
          .finally(() => setCheckingStatus(false));
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => { setShouldRender(false); }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, contact?.phone]);

  if (!shouldRender || !contact) return null;

  const displayName = getDisplayName(contact);
  const formatPhone = (phone) => {
    if (!phone) return '';
    if (phone.startsWith('55') && phone.length >= 12) {
      const ddd = phone.slice(2, 4);
      const part1 = phone.slice(4, phone.length - 4);
      const part2 = phone.slice(-4);
      return `+55 (${ddd}) ${part1}-${part2}`;
    }
    return phone;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Nunca';
    return new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const openWhatsApp = () => {
    const phone = contact.phone?.replace(/\D/g, '');
    window.open(`https://wa.me/${phone}`, '_blank');
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300);
  };

  const handleStopAgent = async () => {
    const cleanPhone = contact?.phone?.replace(/\D/g, '');
    if (!cleanPhone || !targetDbName) return;
    setStoppingAgent(true);
    setStopAgentResult(null);
    try {
      const response = await api.post('/blocking/toggle-ai-block', {
        phoneNumber: cleanPhone,
        action: 'block',
      });
      setStopAgentResult(response.success ? 'success' : 'error');
    } catch {
      setStopAgentResult('error');
    } finally {
      setStoppingAgent(false);
    }
  };

  const color = getAvatarColor(displayName);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${isAnimating ? 'bg-black/60' : 'bg-black/0'}`}
      onClick={handleClose}
    >
      <div
        className={`relative bg-surface-container rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-white/5 transition-all duration-300 ${isAnimating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-zinc-300 hover:bg-white/20 transition-colors"
        >
          <span className="material-symbols-outlined text-base">close</span>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-primary/20 to-primary-container/10 px-6 py-8 text-center border-b border-white/5">
          <div className={`mx-auto mb-4 ${color.bg} ${color.text} w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold transition-all duration-500 delay-100 ${isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
            {displayName.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <h2 className={`text-xl font-bold text-on-surface transition-all duration-500 delay-150 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            {displayName}
          </h2>
        </div>

        {/* Content */}
        <div className="p-6 space-y-3">
          <div className={`flex items-center gap-3 p-3 bg-surface-container-high rounded-xl transition-all duration-500 delay-200 ${isAnimating ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
            <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
              <span className="material-symbols-outlined text-primary text-base">call</span>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Telefone</p>
              <p className="text-sm font-medium text-on-surface">{formatPhone(contact.phone)}</p>
            </div>
          </div>

          <div className={`flex items-center gap-3 p-3 bg-surface-container-high rounded-xl transition-all duration-500 delay-300 ${isAnimating ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
            <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
              <span className="material-symbols-outlined text-primary text-base">schedule</span>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Último contato</p>
              <p className="text-sm font-medium text-on-surface">{formatDate(contact.last_message_at)}</p>
            </div>
          </div>

          <button
            onClick={openWhatsApp}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-primary hover:bg-primary/90 text-on-primary font-semibold rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Abrir no WhatsApp
          </button>

          <button
            onClick={handleStopAgent}
            disabled={stoppingAgent || checkingStatus || stopAgentResult === 'success'}
            className={`w-full flex items-center justify-center gap-2 py-3 px-4 font-semibold rounded-xl border transition-all duration-500 delay-[500ms] ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${
              stopAgentResult === 'success'
                ? 'bg-surface-container-high border-outline-variant/10 text-on-surface-variant cursor-default'
                : 'bg-error/20 border-error/40 text-error hover:bg-error/30 hover:scale-[1.02] active:scale-[0.98]'
            } disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100`}
          >
            {stoppingAgent || checkingStatus ? (
              <div className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1" }}>
                {stopAgentResult === 'success' ? 'check_circle' : 'pause_circle'}
              </span>
            )}
            {stopAgentResult === 'success'
              ? 'Agente pausado por 3h'
              : stopAgentResult === 'error'
              ? 'Erro — tentar novamente'
              : 'Parar agente (3h)'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente de Lista de Conversas
const ConversationList = ({ contacts, selectedPhone, onSelectContact, loading }) => {
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    let date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      date = new Date(String(timestamp).replace(' ', 'T'));
    }
    date = new Date(date.getTime() - 3 * 60 * 60 * 1000);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today - msgDate) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return date.toLocaleDateString('pt-BR', { weekday: 'short' });
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-zinc-500 px-4">
        <span className="material-symbols-outlined text-4xl mb-2">chat_bubble</span>
        <p className="text-sm text-center">Nenhuma conversa ainda</p>
        <p className="text-xs text-center mt-1 text-on-surface-variant">As mensagens aparecerão aqui</p>
      </div>
    );
  }

  return (
    <div>
      {contacts.map((contact) => {
        const isSelected = selectedPhone === contact.phone;
        const name = getDisplayName(contact);
        const time = formatTime(contact.last_message_at);
        const hasUnread = contact.unread_count > 0;
        return (
          <div
            key={contact.id}
            onClick={() => onSelectContact(contact)}
            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
              ${isSelected
                ? 'bg-primary/5 border-l-2 border-primary'
                : 'border-l-2 border-transparent hover:bg-on-surface/5'
              }`}
          >
            <ContactAvatar name={name} phone={contact.phone} imageUrl={contact.profile_picture_url} />
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-baseline">
                <h3 className="text-sm font-semibold truncate text-on-surface">{name}</h3>
                <span className={`text-[10px] whitespace-nowrap ml-2 ${hasUnread ? 'text-primary' : 'text-on-surface-variant'}`}>
                  {time}
                </span>
              </div>
              <div className="flex justify-between items-center mt-0.5">
                <p className="text-xs text-on-surface-variant truncate">{contact.last_message_preview || 'Sem mensagens'}</p>
                {hasUnread && (
                  <span className="bg-primary text-on-primary text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center ml-2 flex-shrink-0">
                    {contact.unread_count}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Componente de Balão de Mensagem
const MessageBubble = ({ message }) => {
  const isFromMe = message.is_from_me || message.direction === 'outgoing';
  const [isVisible, setIsVisible] = React.useState(false);
  const isPending = message.status === 'pending' || message.id?.toString().startsWith('temp_');

  React.useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    let date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      date = new Date(String(timestamp).replace(' ', 'T'));
    }
    date = new Date(date.getTime() - 3 * 60 * 60 * 1000);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const renderStatus = () => {
    if (!isFromMe) return null;
    switch (message.status) {
      case 'pending':
        return <span className="material-symbols-outlined text-xs opacity-60" style={{ fontSize: '12px' }}>schedule</span>;
      case 'sent':
        return <span className="material-symbols-outlined text-xs opacity-70" style={{ fontSize: '12px' }}>done</span>;
      case 'delivered':
        return <span className="material-symbols-outlined text-xs opacity-70" style={{ fontSize: '12px' }}>done_all</span>;
      case 'read':
        return <span className="material-symbols-outlined text-xs" style={{ fontSize: '12px' }}>done_all</span>;
      case 'failed':
        return <span className="material-symbols-outlined text-xs text-error" style={{ fontSize: '12px' }}>error</span>;
      default:
        return null;
    }
  };

  const getAudioDuration = (content) => {
    const match = content?.match(/(\d+)s?\]/);
    return match ? parseInt(match[1]) : null;
  };

  const isMediaPlaceholder = (content) => content?.startsWith('[') && content?.endsWith(']');

  const renderContent = () => {
    const content = message.content || '';
    const isPttAudio     = content.includes('Áudio de voz') || content.includes('áudio de voz');
    const isAudioPh      = content.includes('[Áudio') || content.includes('[Audio');
    const isImagePh      = content.includes('[Imagem');
    const isVideoPh      = content.includes('[Vídeo') || content.includes('[Video');
    const isDocPh        = content.includes('[Documento');
    const isStickerPh    = content.includes('[Sticker');
    const isLocationPh   = content.includes('[Localização') || content.includes('[Localizacao');
    const isContactPh    = content.includes('[Contato');

    // Áudio / PTT
    if (message.message_type === 'audio' || message.message_type === 'ptt' || isAudioPh || isPttAudio) {
      const duration = getAudioDuration(content);
      const isPtt = isPttAudio || message.message_type === 'ptt';
      return (
        <div className="flex items-center gap-3 min-w-[180px]">
          <button className="w-9 h-9 rounded-full bg-current/20 hover:bg-current/30 flex items-center justify-center flex-shrink-0 transition-colors" style={{ color: 'inherit' }}>
            <span className="material-symbols-outlined text-base" style={{ color: 'inherit' }}>{isPtt ? 'mic' : 'play_arrow'}</span>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: 'currentColor', opacity: 0.2 }}>
                <div className="h-full rounded-full w-0" style={{ backgroundColor: 'currentColor', opacity: 0.7 }} />
              </div>
              {duration && (
                <span className="text-[10px] tabular-nums font-semibold">
                  {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Imagem
    if (message.message_type === 'image' || isImagePh) {
      return (
        <div>
          {message.media_url ? (
            <img
              src={message.media_url}
              alt="Imagem"
              className="max-w-xs rounded-lg mb-1 cursor-pointer hover:opacity-90"
              onClick={() => window.open(message.media_url, '_blank')}
            />
          ) : (
            <div className="bg-white/5 rounded-lg flex flex-col items-center gap-2 p-3 min-w-[120px]">
              <span className="material-symbols-outlined text-2xl text-zinc-500/50">image</span>
              <span className="text-[10px] text-zinc-500/70">Imagem</span>
            </div>
          )}
          {content && !isMediaPlaceholder(content) && <p className="text-sm mt-1">{content}</p>}
        </div>
      );
    }

    // Vídeo
    if (message.message_type === 'video' || isVideoPh) {
      return (
        <div className="flex items-center gap-2 bg-white/5 p-3 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
          <span className="material-symbols-outlined text-2xl text-zinc-400">videocam</span>
          <span className="text-sm text-zinc-300">Vídeo</span>
        </div>
      );
    }

    // Documento
    if (message.message_type === 'document' || isDocPh) {
      return (
        <div className="flex items-center gap-2 bg-white/5 p-3 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
          <span className="material-symbols-outlined text-2xl text-orange-400">description</span>
          <span className="text-sm text-zinc-300 truncate">{message.media_filename || 'Documento'}</span>
        </div>
      );
    }

    // Sticker
    if (message.message_type === 'sticker' || isStickerPh) {
      return (
        <div className="flex items-center gap-2 p-2">
          <span className="text-4xl">🎭</span>
          <span className="text-sm">Figurinha</span>
        </div>
      );
    }

    // Localização
    if (message.message_type === 'location' || isLocationPh) {
      return (
        <div className="flex items-center gap-2 bg-white/5 p-3 rounded-lg">
          <span className="material-symbols-outlined text-2xl text-red-400">location_on</span>
          <span className="text-sm text-zinc-300">Localização compartilhada</span>
        </div>
      );
    }

    // Contato
    if (message.message_type === 'contact' || isContactPh) {
      return (
        <div className="flex items-center gap-2 bg-white/5 p-3 rounded-lg">
          <span className="material-symbols-outlined text-2xl text-indigo-400">person</span>
          <span className="text-sm text-zinc-300">Contato compartilhado</span>
        </div>
      );
    }

    // Texto padrão
    return <p className="text-[15px] whitespace-pre-wrap leading-snug">{content}</p>;
  };

  const bubbleReceivedStyle = {
    backgroundColor: 'var(--color-surface-container)',
    borderRadius: '0 8px 8px 8px',
    border: '1px solid var(--color-outline-variant)',
  };

  const bubbleSentStyle = {
    background: 'linear-gradient(135deg, #51e1a5 0%, #1fa86e 100%)',
    borderRadius: '8px 0 8px 8px',
  };

  const animationStyle = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.97)',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
  };

  return (
    <div className={`flex ${isFromMe ? 'justify-end' : 'justify-start'} mb-1`} style={animationStyle}>
      <div
        className={`max-w-[75%] px-4 py-3 shadow-sm ${isFromMe ? 'text-on-primary' : 'text-on-surface'} ${isPending ? 'opacity-70' : ''}`}
        style={isFromMe ? bubbleSentStyle : bubbleReceivedStyle}
      >
        {renderContent()}
        <div className={`flex items-center justify-end gap-1 mt-1.5 ${isFromMe ? 'text-on-primary/70' : 'text-on-surface/50'}`}>
          <span className="text-[11px]">{formatTime(message.timestamp)}</span>
          {renderStatus()}
        </div>
      </div>
    </div>
  );
};

// Componente de Janela de Chat
const ChatWindow = ({ contact, messages, loading, onSendMessage, onBack, targetDbName }) => {
  const [inputMessage, setInputMessage] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [prevContactPhone, setPrevContactPhone] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (contact && contact.phone !== prevContactPhone) {
      setIsAnimating(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
          setPrevContactPhone(contact.phone);
        });
      });
    }
  }, [contact, prevContactPhone]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    onSendMessage(inputMessage.trim());
    setInputMessage('');
    inputRef.current?.focus();
  };

  if (!contact) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-background">
        <div className="w-20 h-20 rounded-full bg-surface-container-high flex items-center justify-center mb-4">
          <span className="material-symbols-outlined text-4xl text-on-surface-variant">chat_bubble</span>
        </div>
        <h2 className="text-lg font-semibold text-on-surface font-headline">Chat WhatsApp</h2>
        <p className="text-sm text-on-surface-variant mt-1">Selecione uma conversa</p>
      </div>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col h-full overflow-hidden transition-opacity duration-300"
      style={{ opacity: isAnimating ? 1 : 0 }}
    >
      {/* Chat header */}
      <header
        className="h-16 flex items-center justify-between px-6 border-b border-outline/10 z-10 flex-shrink-0"
        style={{ backgroundColor: 'var(--color-surface-container-low)' }}
      >
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-surface-container-high transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
          )}
          <button
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            onClick={() => setShowContactModal(true)}
          >
            <ContactAvatar
              name={contact.name || contact.push_name}
              phone={contact.phone}
              imageUrl={contact.profile_picture_url}
            />
            <div className="text-left">
              <h3 className="text-sm font-bold text-on-surface truncate max-w-[200px]">{getDisplayName(contact)}</h3>
              <p className="text-[10px] text-on-surface-variant">{contact.phone}</p>
            </div>
          </button>
        </div>
        <button onClick={() => setShowContactModal(true)} className="text-on-surface-variant hover:text-on-surface transition-colors">
          <span className="material-symbols-outlined">more_vert</span>
        </button>
      </header>

      {/* Contact modal */}
      <ContactInfoModal
        contact={contact}
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        targetDbName={targetDbName}
      />

      {/* Messages area */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-1 min-h-0 relative"
        style={{
          backgroundColor: 'var(--color-surface-container-lowest)',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cpath fill='%2351e1a5' fill-opacity='0.03' d='M0 0h80v80H0z'/%3E%3Cpath fill='none' stroke='%2351e1a5' stroke-opacity='0.04' stroke-width='0.5' d='M0 10h80M0 20h80M0 30h80M0 40h80M0 50h80M0 60h80M0 70h80M10 0v80M20 0v80M30 0v80M40 0v80M50 0v80M60 0v80M70 0v80'/%3E%3C/svg%3E")`,
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--color-surface-variant) transparent',
        }}
      >
        <div className="relative z-10">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-on-surface-variant">
              <span className="material-symbols-outlined text-4xl mb-2">chat</span>
              <p className="text-sm">Nenhuma mensagem ainda</p>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => (
                <MessageBubble key={msg.id || index} message={msg} />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Input area */}
      <footer className="p-4 border-t border-outline/10 flex-shrink-0" style={{ backgroundColor: 'var(--color-surface-container-low)' }}>
        <form onSubmit={handleSend} className="flex items-center gap-3">
          <button type="button" className="text-on-surface-variant hover:text-on-surface transition-colors">
            <span className="material-symbols-outlined">mood</span>
          </button>
          <button type="button" className="text-on-surface-variant hover:text-on-surface transition-colors">
            <span className="material-symbols-outlined">attach_file</span>
          </button>
          <div className="flex-1">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Digite uma mensagem"
              className="w-full bg-surface-container-highest border-none rounded-lg py-2.5 px-4 text-sm text-on-surface focus:ring-1 focus:ring-primary focus:outline-none placeholder-zinc-500"
            />
          </div>
          <button
            type="submit"
            disabled={!inputMessage.trim()}
            className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-on-primary shadow-lg shadow-primary/20 hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1" }}>send</span>
          </button>
        </form>
      </footer>
    </div>
  );
};

// Componente Principal
const Chats = () => {
  const { user } = useAuth();
  const { isConnected, onNewMessage, onContactUpdate } = useSocket();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const selectedPhoneRef = useRef(null);
  const viewedPhonesRef = useRef(new Set());
  const [messages, setMessages] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const pollInterval = useRef(null);
  const selectedContactRef = useRef(null);

  const hasContactsChanged = useCallback((oldContacts, newContacts) => {
    if (oldContacts.length !== newContacts.length) return true;
    for (let i = 0; i < newContacts.length; i++) {
      const oldContact = oldContacts.find(c => c.phone === newContacts[i].phone);
      if (!oldContact) return true;
      if (
        oldContact.last_message_at !== newContacts[i].last_message_at ||
        oldContact.unread_count !== newContacts[i].unread_count ||
        oldContact.last_message_preview !== newContacts[i].last_message_preview
      ) return true;
    }
    return false;
  }, []);

  const hasMessagesChanged = useCallback((oldMessages, newMessages) => {
    if (oldMessages.length !== newMessages.length) return true;
    if (newMessages.length > 0 && oldMessages.length > 0) {
      const lastNew = newMessages[newMessages.length - 1];
      const lastOld = oldMessages[oldMessages.length - 1];
      if (lastNew.id !== lastOld.id || lastNew.status !== lastOld.status) return true;
    }
    return false;
  }, []);

  const isFirstLoad = useRef(true);

  const loadContacts = useCallback(async () => {
    try {
      const response = await api.get('/chat/contacts');
      if (response.success) {
        const rawContacts = response.contacts || [];
        const newContacts = rawContacts.map(c =>
          viewedPhonesRef.current.has(c.phone) ? { ...c, unread_count: 0 } : c
        );
        setContacts(prevContacts => {
          if (isFirstLoad.current || prevContacts.length === 0) return newContacts;
          if (hasContactsChanged(prevContacts, newContacts)) return newContacts;
          return prevContacts;
        });
      }
    } catch (err) {
      console.error('Erro ao carregar contatos:', err);
      setError('Erro ao carregar conversas');
    } finally {
      if (isFirstLoad.current) isFirstLoad.current = false;
      setLoadingContacts(false);
    }
  }, [hasContactsChanged]);

  const isFirstMessagesLoad = useRef(true);

  const loadMessages = useCallback(async (phone, isInitialLoad = false) => {
    if (!phone) return;
    if (isInitialLoad) {
      setLoadingMessages(true);
      isFirstMessagesLoad.current = true;
    }
    try {
      const response = await api.get(`/chat/messages/${phone}`);
      if (response.success) {
        const newMessages = response.messages || [];
        setMessages(prevMessages => {
          const tempMessages = prevMessages.filter(m => m.id?.toString().startsWith('temp_'));
          if (isFirstMessagesLoad.current || prevMessages.length === 0) {
            isFirstMessagesLoad.current = false;
            return [...newMessages, ...tempMessages.filter(tm => !newMessages.some(nm => nm.content === tm.content && nm.is_from_me))];
          }
          if (hasMessagesChanged(prevMessages.filter(m => !m.id?.toString().startsWith('temp_')), newMessages)) {
            return [...newMessages, ...tempMessages.filter(tm => !newMessages.some(nm => nm.content === tm.content && nm.is_from_me))];
          }
          return prevMessages;
        });
      }
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, [hasMessagesChanged]);

  const sendMessage = useCallback(async (text) => {
    if (!selectedContact || !text) return;
    try {
      const tempMessage = {
        id: `temp_${Date.now()}`,
        message_id: `temp_${Date.now()}`,
        direction: 'outgoing',
        message_type: 'text',
        content: text,
        timestamp: new Date().toISOString(),
        status: 'pending',
        is_from_me: true
      };
      setMessages(prev => [...prev, tempMessage]);

      const response = await api.post('/chat/send', { phone: selectedContact.phone, message: text });
      if (response.success) {
        setMessages(prev => prev.map(m => m.id === tempMessage.id ? { ...m, ...response.data, status: 'sent' } : m));
        loadContacts();
      }
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setMessages(prev => prev.map(m => m.id?.toString().startsWith('temp_') ? { ...m, status: 'failed' } : m));
    }
  }, [selectedContact, loadContacts]);

  const selectContact = useCallback((contact) => {
    selectedPhoneRef.current = contact.phone;
    viewedPhonesRef.current.add(contact.phone);
    setSelectedContact(contact);
    setShowChatOnMobile(true);
    loadMessages(contact.phone, true);
    setContacts(prev => prev.map(c => viewedPhonesRef.current.has(c.phone) ? { ...c, unread_count: 0 } : c));
    const url = new URL(window.location.href);
    if (url.searchParams.has('phone')) {
      url.searchParams.delete('phone');
      window.history.replaceState({}, '', url.pathname);
    }
  }, [loadMessages]);

  const handleBackToList = useCallback(() => {
    setShowChatOnMobile(false);
  }, []);

  const [isLoadingFromNotification, setIsLoadingFromNotification] = useState(false);
  const [notificationPhone, setNotificationPhone] = useState(null);
  const [notificationId, setNotificationId] = useState(null);
  const hasAutoSelectedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const phoneFromUrl = params.get('phone');
    const notifIdFromUrl = params.get('notificationId');
    if (phoneFromUrl && !hasAutoSelectedRef.current) {
      setIsLoadingFromNotification(true);
      setNotificationPhone(phoneFromUrl);
      if (notifIdFromUrl) setNotificationId(notifIdFromUrl);
    }
  }, []);

  useEffect(() => {
    if (notificationPhone && contacts.length > 0 && !hasAutoSelectedRef.current) {
      const contactToSelect = contacts.find(c =>
        c.phone?.includes(notificationPhone) || notificationPhone.includes(c.phone?.replace(/\D/g, ''))
      );
      if (contactToSelect) {
        hasAutoSelectedRef.current = true;
        if (notificationId) {
          api.markNotificationAsRead(notificationId).catch(err => {
            console.error('Erro ao marcar notificação como lida:', err);
          });
        }
        setTimeout(() => {
          selectContact(contactToSelect);
          setIsLoadingFromNotification(false);
          setNotificationPhone(null);
          setNotificationId(null);
        }, 800);
      } else {
        setIsLoadingFromNotification(false);
        hasAutoSelectedRef.current = true;
      }
    }
  }, [notificationPhone, contacts, selectContact, notificationId]);

  useEffect(() => {
    selectedContactRef.current = selectedContact;
  }, [selectedContact]);

  useEffect(() => {
    console.log('💬 Chats: Registrando listener Socket.IO para mensagens');
    const unsubscribeMessage = onNewMessage((messageData) => {
      console.log('💬 Chats: Nova mensagem recebida via Socket.IO:', messageData);
      const currentContact = selectedContactRef.current;
      if (currentContact && messageData.contact_phone === currentContact.phone) {
        setMessages(prev => {
          const exists = prev.some(m => m.message_id === messageData.message_id || m.id === messageData.id);
          if (exists) return prev;
          return [...prev, messageData];
        });
      }
    });

    const unsubscribeContact = onContactUpdate((contactData) => {
      console.log('📇 Chats: Atualização de contato via Socket.IO:', contactData);
      setContacts(prev => {
        const index = prev.findIndex(c => c.phone === contactData.phone);
        if (index === -1) return [{ ...contactData, id: Date.now() }, ...prev];
        const updated = [...prev];
        const existing = updated.splice(index, 1)[0];
        const merged = {
          ...existing,
          last_message_at: contactData.last_message_at,
          last_message_preview: contactData.last_message_preview,
          unread_count: (existing.unread_count || 0) + (contactData.unread_count || 0)
        };
        return [merged, ...updated];
      });
    });

    return () => {
      console.log('💬 Chats: Removendo listeners Socket.IO');
      unsubscribeMessage();
      unsubscribeContact();
    };
  }, [onNewMessage, onContactUpdate]);

  useEffect(() => {
    loadContacts();
    const pollIntervalMs = isConnected ? 60000 : 10000;
    console.log(`⏰ Chats: Polling configurado para ${pollIntervalMs / 1000}s (Socket.IO ${isConnected ? 'conectado' : 'desconectado'})`);
    pollInterval.current = setInterval(() => {
      loadContacts();
      if (selectedContact) loadMessages(selectedContact.phone);
    }, pollIntervalMs);
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [loadContacts, loadMessages, selectedContact, isConnected]);

  // Loading screen ao vir de notificação
  if (isLoadingFromNotification) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-background">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 w-24 h-24 mx-auto rounded-full bg-primary/20 animate-ping" style={{ animationDuration: '1.5s' }} />
            <div className="relative w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-primary to-primary-container flex items-center justify-center shadow-lg shadow-primary/30">
              <span className="material-symbols-outlined text-on-primary text-5xl" style={{ fontVariationSettings: "'FILL' 1" }}>chat_bubble</span>
            </div>
          </div>
          <h2 className="text-lg font-semibold text-on-surface mb-1 font-headline animate-pulse">Abrindo conversa...</h2>
          <p className="text-sm text-zinc-500">Carregando mensagens</p>
          <div className="flex justify-center items-center gap-2 mt-5">
            {[0, 150, 300].map((delay) => (
              <div key={delay} className="w-2.5 h-2.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: `${delay}ms` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-background">
        <div className="text-center">
          <span className="material-symbols-outlined text-5xl text-error mb-3 block">error</span>
          <p className="text-on-surface mb-4">{error}</p>
          <button
            onClick={() => { setError(null); loadContacts(); }}
            className="px-5 py-2 bg-primary text-on-primary rounded-lg hover:bg-primary/90 text-sm font-medium transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  const filteredContacts = contacts.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = (c.name || c.push_name || '').toLowerCase();
    const phone = (c.phone || '').replace(/\D/g, '');
    const searchPhone = term.replace(/\D/g, '');
    return name.includes(term) || phone.includes(searchPhone);
  });

  return (
    <div className="flex h-full w-full overflow-hidden bg-background">
      {/* Contact list panel */}
      <div
        className={`flex flex-col h-full flex-shrink-0 border-r border-outline/10 transition-all duration-300
          ${showChatOnMobile ? 'hidden md:flex md:w-80' : 'w-full md:w-80'}`}
        style={{ backgroundColor: 'var(--color-surface-container-low)' }}
      >
        {/* Panel header */}
        <div className="flex-none px-4 py-3 border-b border-outline/10">
          <div className={`flex items-center justify-between transition-all duration-300 ${isSearchOpen ? 'mb-3' : ''}`}>
            <div>
              <h2 className="text-base font-bold text-on-surface font-headline">Conversas</h2>
              <p className="text-[10px] text-on-surface-variant uppercase tracking-widest font-bold mt-0.5">
                {searchTerm
                  ? `${filteredContacts.length} de ${contacts.length} conversas`
                  : `${contacts.length} conversas`}
              </p>
            </div>
            <button
              onClick={() => {
                setIsSearchOpen(!isSearchOpen);
                if (!isSearchOpen) setTimeout(() => searchInputRef.current?.focus(), 100);
                else setSearchTerm('');
              }}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${isSearchOpen ? 'bg-primary text-on-primary' : 'text-zinc-400 hover:bg-surface-container-high'}`}
            >
              <span className="material-symbols-outlined text-base">search</span>
            </button>
          </div>

          {/* Search input */}
          <div className={`overflow-hidden transition-all duration-300 ${isSearchOpen ? 'max-h-12 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-base">search</span>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome ou número..."
                className="w-full pl-9 pr-9 py-2 bg-surface-container-highest border-none rounded-lg text-sm text-on-surface placeholder-zinc-500 focus:ring-1 focus:ring-primary focus:outline-none"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Contact list */}
        <div className="flex-1 overflow-y-auto min-h-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#353437 transparent' }}>
          <ConversationList
            contacts={filteredContacts}
            selectedPhone={selectedContact?.phone}
            onSelectContact={selectContact}
            loading={loadingContacts}
          />
        </div>
      </div>

      {/* Chat window */}
      <div className={`flex-1 h-full transition-all duration-300 ${showChatOnMobile ? 'flex' : 'hidden md:flex'}`}>
        <ChatWindow
          contact={selectedContact}
          messages={messages}
          loading={loadingMessages}
          onSendMessage={sendMessage}
          onBack={handleBackToList}
          targetDbName={user?.target_db_name}
        />
      </div>
    </div>
  );
};

export default Chats;
